import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CourseReviewDecision,
  CourseReviewQueueItem,
  CourseSubmissionView,
  decideCourseReview,
  getCourseSubmission,
  listCourseReviews,
} from '../api/course-reviews';
import { CourseSchedule, describeSchedule } from '../domain/catalog';
import { formatMoney } from '../../../commodity/format/money';
import {
  Drawer,
  Empty,
  Field,
  Note,
  OpsPage,
  Pill,
  Row,
  SlaTag,
  Table,
  Value,
} from '../../../design-system/ops';

/**
 * The course review queue and decision — UC-A08 (ADM-08-FR-02, ADM-08-FR-05).
 *
 * The same submissions appear in the operations queue (`/admin/cases`), which
 * owns the case and its SLA (AD-A08-14); each case links here, where the
 * decision is taken against the listing itself.
 *
 * What the reviewer reads is the frozen version the teacher sent, not the
 * course as it stands now (AC-01), and every decision carries that version's
 * id so a stale screen is refused rather than applied (A13).
 */

const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString('es-MX') : '—');

const DECISION_LABEL: Record<CourseReviewDecision, string> = {
  APPROVED: 'Aprobado',
  CHANGES_REQUIRED: 'Cambios solicitados',
  REJECTED: 'Rechazado',
};

/** The API's own minimum; checked here only so the button says so before the call. */
const MIN_FEEDBACK = 10;

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

const AdminCourseReviews: React.FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId?: string }>();

  const [queue, setQueue] = useState<CourseReviewQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<CourseSubmissionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      setQueue((await listCourseReviews()).submissions);
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar la cola de revisión.');
    } finally {
      setLoading(false);
    }
  }, []);

  const open = useCallback(async (id: string) => {
    setError(null);
    setFeedback('');
    try {
      setSelected(await getCourseSubmission(id));
    } catch (e: any) {
      setError(e?.message || 'No se pudo abrir la versión enviada.');
    }
  }, []);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  // A case in the ops queue links straight to its course.
  useEffect(() => {
    if (courseId) void open(courseId);
  }, [courseId, open]);

  const close = () => {
    setSelected(null);
    if (courseId) navigate('/admin/course-reviews');
  };

  const decide = async (decision: CourseReviewDecision) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const outcome = await decideCourseReview(selected.listing.listingId, {
        courseVersionId: selected.version.courseVersionId,
        decision,
        ...(feedback.trim() ? { feedback: feedback.trim() } : {}),
      });
      const title = selected.listing.title;
      if (decision === 'APPROVED' && outcome.publication) {
        // AD-A08-05: approval stands even when publication is held back, and
        // the reviewer is told which gate held it.
        setInfo(
          outcome.publication.published
            ? `“${title}” aprobado y publicado.`
            : `“${title}” aprobado, pero no se publicó: ${outcome.publication.blockedBy}. ` +
                'El profesor podrá publicarlo cuando lo resuelva.',
        );
      } else {
        setInfo(`“${title}”: ${DECISION_LABEL[decision].toLowerCase()}.`);
      }
      close();
      await loadQueue();
    } catch (e: any) {
      setError(e?.message || 'No se pudo registrar la decisión.');
    } finally {
      setBusy(false);
    }
  };

  const pending = selected?.listing.reviewStatus === 'PENDING_REVIEW';
  const feedbackTooShort = feedback.trim().length < MIN_FEEDBACK;
  const noResubmissionsLeft = selected ? selected.resubmissionsRemaining <= 0 : false;

  return (
    <OpsPage
      title="Revisión de cursos"
      subtitle="Cada curso espera aquí hasta que alguien revise exactamente la versión enviada. El plazo objetivo avisa; nunca decide."
    >
      {error ? <Note tone="bad">{error}</Note> : null}
      {info ? <Note tone="warn">{info}</Note> : null}

      {loading ? (
        <Note>Cargando…</Note>
      ) : queue.length === 0 ? (
        <Empty icon="📚" title="Sin cursos en revisión" detail="No hay envíos esperando." />
      ) : (
        <Table columns="1fr 160px 170px 150px" head={['Curso', 'Versión', 'Enviado', 'Plazo']}>
          {queue.map((item) => (
            <Row
              key={item.courseId}
              onOpen={() => open(item.courseId)}
              label={`Revisar el curso ${item.title}`}
            >
              <span>
                <b>{item.title}</b>
                <br />
                <span className="mut">{item.subject}</span>
              </span>
              <span className="mut">
                v{item.version}
                {item.resubmissionCount > 0 ? ` · reenvío ${item.resubmissionCount}` : ''}
              </span>
              <span className="mut">{fmt(item.submittedAt)}</span>
              <span>
                <SlaTag
                  hoursRemaining={item.sla.hoursRemaining}
                  dueSoonHours={item.sla.dueSoonHours}
                />
                {item.sla.overdue ? (
                  <>
                    {' '}
                    <Pill tone="bad">Vencido</Pill>
                  </>
                ) : null}
              </span>
            </Row>
          ))}
        </Table>
      )}

      <Drawer
        open={selected !== null}
        onClose={close}
        title={selected ? selected.listing.title : 'Curso'}
        meta={selected ? `Versión enviada ${selected.version.courseVersionId}` : undefined}
        footer={
          selected && pending ? (
            <>
              <button
                className="btn btn-o"
                disabled={busy || feedbackTooShort || noResubmissionsLeft}
                title={
                  noResubmissionsLeft
                    ? 'No quedan reenvíos: aprueba o rechaza.'
                    : feedbackTooShort
                    ? 'Escribe qué debe cambiar el profesor.'
                    : undefined
                }
                onClick={() => decide('CHANGES_REQUIRED')}
              >
                Pedir cambios
              </button>
              <button
                className="btn btn-d"
                disabled={busy || feedbackTooShort}
                onClick={() => decide('REJECTED')}
              >
                Rechazar
              </button>
              <button className="btn btn-p" disabled={busy} onClick={() => decide('APPROVED')}>
                Aprobar
              </button>
            </>
          ) : null
        }
      >
        {selected ? <SubmissionDetail view={selected} /> : null}

        {selected && pending ? (
          <Field label="Comentario para el profesor">
            <textarea
              id="course-review-feedback"
              className="inp"
              rows={4}
              value={feedback}
              aria-label="Comentario para el profesor"
              placeholder="Qué debe cambiar, en palabras que el profesor pueda aplicar."
              onChange={(e) => setFeedback(e.target.value)}
            />
            {feedbackTooShort ? (
              <Note>
                Pedir cambios o rechazar necesita un comentario de al menos {MIN_FEEDBACK}{' '}
                caracteres. El profesor lo leerá tal cual.
              </Note>
            ) : null}
            {noResubmissionsLeft ? (
              <Note tone="warn">
                Este curso ya usó sus {selected.maxResubmissions} reenvíos. Solo puedes aprobarlo o
                rechazarlo.
              </Note>
            ) : null}
          </Field>
        ) : null}
      </Drawer>
    </OpsPage>
  );
};

/** The frozen content, field by field, as the teacher sent it. */
const SubmissionDetail: React.FC<{ view: CourseSubmissionView }> = ({ view }) => {
  const c = view.version.content;
  const objectives = Array.isArray(c.learningObjectives) ? (c.learningObjectives as string[]) : [];
  const grades = Array.isArray(c.gradeLevel) ? (c.gradeLevel as string[]) : [];
  const schedule = c.schedule as CourseSchedule | undefined;

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Pill tone="tint">v{view.version.version}</Pill>
        {view.sla ? (
          <SlaTag hoursRemaining={view.sla.hoursRemaining} dueSoonHours={view.sla.dueSoonHours} />
        ) : (
          <Pill tone="neu">{view.listing.reviewStatus ?? '—'}</Pill>
        )}
        <Pill tone="neu">
          Reenvíos: {view.listing.resubmissionCount ?? 0} de {view.maxResubmissions}
        </Pill>
      </div>

      <Field label="Título">
        <Value>{text(c.title) ?? '—'}</Value>
      </Field>
      <Field label="Descripción">
        <Value>{text(c.description) ?? '—'}</Value>
      </Field>
      <Field label="Resumen de la clase">
        <Value>{text(c.overview) ?? 'Falta'}</Value>
      </Field>
      <Field label="Objetivos de aprendizaje">
        {objectives.length ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {objectives.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
        ) : (
          <Value>Falta</Value>
        )}
      </Field>
      <Field label="Estructura de la clase">
        <Value>{text(c.classStructure) ?? 'Falta'}</Value>
      </Field>
      {/* BR-A08-02: optional, so its absence is stated rather than flagged. */}
      <Field label="Qué esperar (opcional)">
        <Value>{text(c.whatToExpect) ?? 'No incluido'}</Value>
      </Field>

      <Field label="Materia, grados e idioma">
        <Value>
          {text(c.category) ?? '—'} · {grades.join(', ') || '—'} · {text(c.language) ?? '—'}
        </Value>
      </Field>
      <Field label="Oferta">
        <Value>
          {c.offerMode === 'ONE_ON_ONE' ? 'Individual' : `Grupo de hasta ${c.maxStudents}`} ·{' '}
          {String(c.duration)} min ·{' '}
          {typeof c.price === 'number'
            ? formatMoney(c.price, c.currency as string | undefined)
            : '—'}
          {schedule ? ` · ${describeSchedule(schedule)}` : ' · por cita'}
          {c.durationMode === 'FIXED' && c.durationWeeks ? ` · ${c.durationWeeks} semanas` : ''}
        </Value>
      </Field>

      {view.decisions.length > 0 ? (
        <Field label="Decisiones anteriores">
          {view.decisions.map((d) => (
            <div key={d.courseVersionId} style={{ padding: '6px 0' }}>
              <b>
                v{d.version} · {DECISION_LABEL[d.decision] ?? d.decision}
              </b>{' '}
              <span className="mut">{fmt(d.decidedAt)}</span>
              {d.feedback ? <div>{d.feedback}</div> : null}
            </div>
          ))}
        </Field>
      ) : null}
    </>
  );
};

export default AdminCourseReviews;
